import { api } from "../../api/client";
import type { ThreatTo } from "../../wizard/ThreatStep";

export type ThreatLevel = "low" | "medium" | "high";
export type POIStatus = "active" | "dormant" | "cleared";
export type POIScopeLevel = "constituency" | "ward" | "branch";
export type Classification = "unclassified" | "restricted" | "secret";

// POI tag vocabulary. Mirror of `POITag` in api/app/models.py — keep the two
// in sync. Adding a tag here is enough; the API stores any string in the
// union and the wizard pulls labels from POI_TAG_LABELS below.
export type POITag =
  | "ally"
  | "opposition"
  | "defector_target"
  | "recruit_target"
  | "informer"
  | "asset"
  | "donor"
  | "popular"
  | "vip"
  | "media_personality"
  | "community_leader"
  | "business_leader"
  | "religious_leader"
  | "traditional_leader"
  | "youth_leader"
  | "elder"
  | "nice_to_have"
  | "threat"
  | "other";

export const POI_TAG_LABELS: Record<POITag, string> = {
  ally: "Ally",
  opposition: "Opposition",
  defector_target: "Defector target",
  recruit_target: "Recruit target",
  informer: "Informer",
  asset: "Asset",
  donor: "Donor",
  popular: "Popular",
  vip: "VIP",
  media_personality: "Media personality",
  community_leader: "Community leader",
  business_leader: "Business leader",
  religious_leader: "Religious leader",
  traditional_leader: "Traditional leader",
  youth_leader: "Youth leader",
  elder: "Elder",
  nice_to_have: "Nice to have",
  threat: "Threat",
  other: "Other",
};

// Render order in the multi-select. Operational tags first, then public-
// profile tags, then catch-alls.
export const POI_TAG_ORDER: POITag[] = [
  "ally", "opposition", "defector_target", "recruit_target", "informer", "asset", "donor",
  "popular", "vip", "media_personality",
  "community_leader", "business_leader", "religious_leader", "traditional_leader", "youth_leader", "elder",
  "nice_to_have", "threat", "other",
];

export type PopularityScore = "very_low" | "low" | "medium" | "high" | "very_high";

export interface POI {
  id: string;
  fullName: string;
  aliases: string[];
  idNumber: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  dateOfBirth: string | null;
  occupation: string | null;
  affiliation: string | null;
  lastKnownLocation: string | null;
  knownAssociates: string | null;
  notes: string;
  threatLevel: ThreatLevel;
  status: POIStatus;
  classification: Classification;
  scopeLevel: POIScopeLevel;
  scopeId: string;
  branchId: string | null;
  wardId: string | null;
  constituencyId: string | null;
  pollingStationId?: string | null;
  tags?: POITag[];
  popularityScore?: PopularityScore | null;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string | null;
}

interface List<T> { items: T[]; }

export interface POICreate {
  fullName: string;
  aliases?: string[];
  idNumber?: string;
  phone?: string;
  email?: string;
  address?: string;
  dateOfBirth?: string;
  occupation?: string;
  affiliation?: string;
  lastKnownLocation?: string;
  knownAssociates?: string;
  notes?: string;
  threatLevel?: ThreatLevel;
  classification?: Classification;
  scopeLevel: POIScopeLevel;
  scopeId: string;
  pollingStationId?: string;
  tags?: POITag[];
  popularityScore?: PopularityScore;
  threatTo?: ThreatTo;
  threatTarget?: string;
  threatDetails?: string;
  sourcesCount?: number;
  confidenceScore?: number;
}

export const POIApi = {
  list: (filters?: { status?: POIStatus; threatLevel?: ThreatLevel; tag?: POITag | POITag[] }) => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.threatLevel) qs.set("threatLevel", filters.threatLevel);
    if (filters?.tag) {
      const t = Array.isArray(filters.tag) ? filters.tag.join(",") : filters.tag;
      qs.set("tag", t);
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<List<POI>>(`/poi${suffix}`);
  },
  get: (id: string) => api<POI>(`/poi/${id}`),
  create: (body: POICreate) =>
    api<POI>("/poi", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Omit<POI, "id">>) =>
    api<POI>(`/poi/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) =>
    api<void>(`/poi/${id}`, { method: "DELETE" }),
};
